/**
 * MAIN APP COMPONENT
 *
 * This is the root component that orchestrates the entire application.
 * It sets up routing, layout, authentication, and navigation.
 *
 * Key Features:
 * - React Router for page navigation (BrowserRouter)
 * - Dynamic layout: logo on home page, navbar on all other pages
 * - Rainbow gradient background spanning all pages
 * - Protected routes using BetaGuard component
 * - Floating "back to top" button on scroll
 * - Authentication modal for sign up/login
 * - Notification toast container for push notifications
 *
 * Architecture:
 * - BrowserRouter wraps everything for routing
 * - ScrollToTop component resets scroll on route changes
 * - AppRouter determines layout based on route (normal vs pop-out chat)
 * - AppContent handles the main layout structure
 *
 * Route Protection:
 * - Public routes: accessible to everyone (e.g., /discover, /profile/username)
 * - Protected routes: wrapped with BetaGuard, requires beta terms acceptance
 */

import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Home from './pages/Home';
import Collection from './pages/Collection';
import PublicCollection from './pages/PublicCollection';
import Profile from './pages/Profile';
import Auth from './components/Auth';
import AuthModal from './components/AuthModal';
import ImportPlates from './pages/ImportPlates';
import FixBrandName from './pages/FixBrandName';
import ResizePhotos from './pages/ResizePhotos';
import ReviewChromeTags from './pages/ReviewChromeTags';
import SwatcherReview from './pages/SwatcherReview';
import SwatcherReviewSwatchOnly from './pages/SwatcherReviewSwatchOnly';
import EditPhotoCaptions from './pages/EditPhotoCaptions';
// ADMIN/MIGRATION TOOLS - Commented out for production launch
// import MigrationAnalyzer from './pages/MigrationAnalyzer';
// import PhotoMigrationTool from './pages/PhotoMigrationTool';
// import ReleaseMigrationTool from './pages/ReleaseMigrationTool';
import Settings from './pages/Settings';
import Marketplace from './pages/Marketplace';
import Messages from './pages/Messages';
import Conversation from './pages/Conversation';
import Creations from './pages/Creations';
import Inspo from './pages/Inspo';
import Navbar from './components/Navbar';
import PublicWishlist from './pages/PublicWishlist.tsx';
import DiscoveryPage from './pages/DiscoveryPage';
import EventsPage from './pages/EventsPage';
import HelpPage from './pages/HelpPage';
import GuidesPage from './pages/GuidesPage';
import FeedbackPage from './pages/FeedbackPage';
import DestashPage from './pages/DestashPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import PublicCreations from './pages/PublicCreations.tsx';
import Footer from './components/Footer';
import FavoriteSellers from './pages/FavoriteSellers';
import PopOutChat from './pages/PopOutChat';
import Wishlist from './pages/Wishlist.tsx';
import ImportPage from './pages/ImportPage';
import ContactPage from './pages/ContactPage';
import { NotificationToastContainer } from './components/NotificationToast';
import { Toaster } from 'react-hot-toast';
import BrandPage from './components/brands/BrandPage';
import BrandsListPage from './components/brands/BrandsListPage';
import BrandClaimPage from './components/brands/BrandClaimPage';
import PopulateBrands from './pages/PopulateBrands';
import MergeBrands from './pages/MergeBrands';
import SetBrandCurationStatus from './pages/SetBrandCurationStatus';
import MergeBrandDocs from './pages/MergeBrandDocs';
import FixOrphanCatalog from './pages/FixOrphanCatalog';
import MergePolishes from './pages/MergePolishes';
import CollectionsCleanup from './pages/CollectionsCleanup';
import PolishNamesCleanup from './pages/PolishNamesCleanup';
import TagPusher from './pages/TagPusher';
import AssignCollections from './pages/AssignCollections';
import InspectReleaseFields from './pages/InspectReleaseFields';
import MigrateEventSource from './pages/MigrateEventSource';
import BackfillShipping from './pages/BackfillShipping';
import PolishDetailPage from './pages/PolishDetailPage';
// Shared detail-page component for all 8 itemTypes. Polish routes go
// through the thin PolishDetailPage wrapper; non-polish routes below
// pass their own `itemType` prop.
import CommunityItemDetailPage from './pages/CommunityItemDetailPage';
import AdminDashboard from './pages/AdminDashboard';
import DataCleanupWizard from './pages/DataCleanupWizard';
import BrandClaimsAdmin from './pages/admin/BrandClaimsAdmin';
import ModerationQueue from './pages/admin/ModerationQueue';
import WIPDashboard from './pages/WIPDashboard';
import BrandManagePage from './components/brands/BrandManagePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import CopyrightPolicyPage from './pages/CopyrightPolicyPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import DMCAClaimForm from './pages/DMCAClaimForm';
import AdminDMCA from './pages/AdminDMCA';
import UpdatesPage from './pages/UpdatesPage';
import AdminUpdates from './pages/AdminUpdates';
import DataGuidePage from './pages/DataGuidePage';
import GracePeriodBanner, { ExpiredSubscriptionBanner } from './components/GracePeriodBanner';
import { useModerationState } from './hooks/useModerationState';
import { BannedScreen, SuspendedBanner } from './components/moderation/ModerationGate';

/**
 * ScrollToTop Component
 *
 * Automatically scrolls to the top of the page whenever the route changes.
 * This ensures users don't land in the middle of a new page.
 *
 * Must be inside BrowserRouter to access location.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/**
 * AppContent Component
 *
 * Main layout container with the full application UI:
 * - Logo on home page only
 * - Navbar on all pages (overlaid on home logo)
 * - Rainbow gradient background
 * - Main content area with routes
 * - Floating back-to-top button (appears after scrolling 400px)
 * - Footer
 * - Auth modal and notification toasts
 *
 * Layout Strategy:
 * - Home page: Logo at top with navbar overlaid, content below
 * - Other pages: Navbar at top, content in main area
 * - All pages share the rainbow gradient background
 */
function AppContent() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Realtime moderation gate. `moderationLoading` briefly holds render
  // for signed-in users so a banned account never sees a flash of the
  // app on cold start. Admin routes (`/admin`, `/admin/*`, `/admin-updates`)
  // stay reachable for `isAdmin` users so an accidental self-moderation
  // doesn't lock the admin out of the console that can undo it. Auth
  // pages (`/auth`) also bypass — the spec says never gate auth.
  const { state: moderationState, isAdmin, loading: moderationLoading } = useModerationState();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAuthRoute = location.pathname === '/auth';
  const bypassGate = isAuthRoute || (isAdmin && isAdminRoute);

  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hold the first frame while the moderation snapshot is in flight for a
  // signed-in user. When there's no uid the hook flips loading→false
  // immediately, so anonymous visitors don't blank-flash. The gate is
  // bypassed on /auth so users can always reach sign-in/out.
  if (moderationLoading && !bypassGate) {
    return null;
  }

  // Full-screen ban replacement — no header, no routes, no footer. Admins
  // on /admin/* skip this so they can still access the moderation console.
  if (moderationState === 'banned' && !bypassGate) {
    return <BannedScreen />;
  }

  const showSuspendedBanner = moderationState === 'suspended' && !bypassGate;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to main content link - visible only when focused */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-slate-800 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-white"
      >
        Skip to main content
      </a>

      {/* Suspended-account banner — sticky above everything (nav, grace
          banner, routes). Non-dismissible; explains why writes will be
          blocked by Firestore rules. */}
      {showSuspendedBanner && <SuspendedBanner />}

      {/* LOGO - Only on Home page */}
      {isHomePage && (
        <div className="w-full relative">
          <img 
            src="/logo.png" 
            alt="Polish ALL the Things! Logo" 
            className="w-full h-auto"
          />
          
          {/* Navbar positioned over logo on home page */}
          <div className="absolute top-0 left-0 right-0 z-50">
            <Navbar onShowAuthModal={() => setShowAuthModal(true)} />
          </div>
        </div>
      )}
      
      {/* RAINBOW GRADIENT BACKGROUND - Starts here */}
      <div className="flex-1 flex flex-col bg-gradient-to-b from-fuchsia-400 via-purple-400 via-blue-400 to-emerald-600">
        
        {/* Navbar for non-home pages */}
        {!isHomePage && (
          <Navbar onShowAuthModal={() => setShowAuthModal(true)} />
        )}

        <GracePeriodBanner />
        <ExpiredSubscriptionBanner />

        {/* MAIN CONTENT */}
        <main id="main-content" className="flex-1 py-8" tabIndex={-1}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<Auth />} />
            
            {/* Protected routes - wrapped with BetaGuard */}
            <Route path="/collection" element={<Collection />} />
            <Route path="/creations" element={<Creations />} />
            <Route path="/inspo" element={<Inspo />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:conversationId" element={<Conversation />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/favorites" element={<FavoriteSellers />} />
            <Route path="/my-wants" element={<Wishlist />} />
            <Route path="/import" element={<ImportPage />} />
            
            {/* Public routes - no BetaGuard needed */}
            <Route path="/collection/:username" element={<PublicCollection />} />
            {/* ADMIN/MIGRATION ROUTES - Commented out for production launch */}
            {/* <Route path="/migrate" element={<MigrationAnalyzer />} /> */}
            {/* <Route path="/admin/photo-migration" element={<PhotoMigrationTool />} /> */}
            {/* <Route path="/migrate-releases" element={<ReleaseMigrationTool />} /> */}
            <Route path="/profile/:username/collection" element={<PublicCollection />} />
            <Route path="/profile/:username/wishlist" element={<PublicCollection />} />
            <Route path="/profile/:username/destash" element={<PublicCollection />} />
            <Route path="/wishlist/:username" element={<PublicWishlist />} />
            <Route path="/discover" element={<DiscoveryPage />} />
            <Route path="/profile/:username" element={<Profile />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/destash/:username" element={<DestashPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/creations/:username" element={<PublicCreations />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/import-plates" element={<ImportPlates />} />
            <Route path="/fix-brand" element={<FixBrandName />} />
            <Route path="/resize-photos" element={<ResizePhotos />} />
            <Route path="/review-chrome" element={<ReviewChromeTags />} />
            <Route path="/review-swatchers" element={<SwatcherReview />} />
            <Route path="/review-swatchers-swatch-only" element={<SwatcherReviewSwatchOnly />} />
            <Route path="/edit-photo-captions" element={<EditPhotoCaptions />} />
            <Route path="/brands" element={<BrandsListPage />} />
            <Route path="/brands/:slug" element={<BrandPage />} />
            <Route path="/brands/:slug/claim" element={<BrandClaimPage />} />
            {/* All 8 community item-detail routes point at the shared
                CommunityItemDetailPage. Every route uses the SAME URL
                param name (`itemSlug`) so the shared component can read
                it uniformly via useParams. `itemType` is passed as a
                prop per route — that's what the component uses to
                switch between polish's polishCatalog query path and
                non-polish's community-aggregation query path (Phase 4). */}
            <Route path="/brands/:brandSlug/polish/:itemSlug" element={<PolishDetailPage />} />
            <Route path="/brands/:brandSlug/plate/:itemSlug" element={<CommunityItemDetailPage itemType="plate" />} />
            <Route path="/brands/:brandSlug/cuticle-care/:itemSlug" element={<CommunityItemDetailPage itemType="cuticle_care" />} />
            <Route path="/brands/:brandSlug/embellishment/:itemSlug" element={<CommunityItemDetailPage itemType="embellishment" />} />
            <Route path="/brands/:brandSlug/transfer/:itemSlug" element={<CommunityItemDetailPage itemType="transfer" />} />
            <Route path="/brands/:brandSlug/powder/:itemSlug" element={<CommunityItemDetailPage itemType="powder" />} />
            <Route path="/brands/:brandSlug/acrylic/:itemSlug" element={<CommunityItemDetailPage itemType="acrylic" />} />
            <Route path="/brands/:brandSlug/tool/:itemSlug" element={<CommunityItemDetailPage itemType="tool" />} />
            <Route path="/admin/populate-brands" element={<PopulateBrands />} />
            <Route path="/admin/merge-brands" element={<MergeBrands />} />
            <Route path="/admin/set-brand-curation" element={<SetBrandCurationStatus />} />
            <Route path="/admin/merge-brand-docs" element={<MergeBrandDocs />} />
            <Route path="/admin/fix-orphan-catalog" element={<FixOrphanCatalog />} />
            <Route path="/admin/merge-polishes" element={<MergePolishes />} />
            <Route path="/admin/collections-cleanup" element={<CollectionsCleanup />} />
            <Route path="/admin/polish-names-cleanup" element={<PolishNamesCleanup />} />
            <Route path="/admin/tag-pusher" element={<TagPusher />} />
            <Route path="/admin/assign-collections" element={<AssignCollections />} />
            <Route path="/admin/inspect-release-fields" element={<InspectReleaseFields />} />
            <Route path="/admin/migrate-event-source" element={<MigrateEventSource />} />
            <Route path="/admin/backfill-shipping" element={<BackfillShipping />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/cleanup" element={<DataCleanupWizard />} />
            <Route path="/admin/brand-claims" element={<BrandClaimsAdmin />} />
            <Route path="/admin/moderation" element={<ModerationQueue />} />
            <Route path="/wip" element={<WIPDashboard />} />
            <Route path="/brands/:slug/manage" element={<BrandManagePage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/copyright" element={<CopyrightPolicyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/dmcaclaim" element={<DMCAClaimForm />} />
            <Route path="/dmca" element={<AdminDMCA />} />
            <Route path="/updates" element={<UpdatesPage />} />
            <Route path="/admin-updates" element={<AdminUpdates />} />
            <Route path="/help/data-guide" element={<DataGuidePage />} />
          </Routes>
        </main>
      
        {/* FLOATING BACK TO TOP BUTTON */}
        {showBackToTop && (
          <button
            className="fixed bottom-24 md:bottom-8 right-4 md:right-8 w-12 h-12 md:w-14 md:h-14 bg-slate-700 text-white rounded-full shadow-lg hover:bg-slate-800 transition-all transform hover:scale-110 flex items-center justify-center text-xl md:text-2xl z-50"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Back to top"
          >
            ↑
          </button>
        )}
      
        {/* FOOTER */}
        <Footer />
      
      </div>
      
      {/* AUTH MODAL */}
      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => window.location.reload()}
        />
      )}
      
      {/* NOTIFICATION TOASTS */}
      <NotificationToastContainer />

      {/* GENERAL TOAST NOTIFICATIONS (success, error, info) */}
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '12px',
            padding: '12px 16px',
          },
        }}
      />
    </div>
  );
}

/**
 * AppRouter Component
 *
 * Determines which layout to use based on the current route.
 *
 * Two Layouts:
 * 1. Pop-out Chat (/messages/:id/popout)
 *    - Minimal layout: just the chat interface
 *    - No navbar, footer, or rainbow background
 *    - Designed to open in a separate window
 *
 * 2. Full Layout (everything else)
 *    - Complete UI with navbar, footer, background
 *    - The standard user experience
 *
 * This separation allows the pop-out chat to be lightweight
 * and focused without unnecessary UI chrome.
 */
function AppRouter() {
  const location = useLocation();

  // Check if this is a pop-out chat route
  const isPopOutChat = location.pathname.includes('/popout');

  // Pop-out chat gets minimal layout (no navbar, footer, rainbow background)
  if (isPopOutChat) {
    return (
      <Routes>
        <Route path="/messages/:conversationId/popout" element={<PopOutChat />} />
      </Routes>
    );
  }

  // Everything else gets the full layout
  return <AppContent />;
}



function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
